import { Link, useLocation } from 'react-router-dom';
import { Box, Button, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';

import { bgColors, glowColor, panelBorder, redColors } from 'theme/colors';

import Logo from '../Logo';
import { getCursor, getDisableRipple, navItems } from '../utils';

const PHONE_NUMBER = '0452393591';
const PHONE_NUMBER_DISPLAY = '045 239 3591';
const PRIMARY_COLOR = 'primary.main';
const LIGHT_TEXT_COLOR = 'typography.light';
const PHONE_GRADIENT = `linear-gradient(90deg, ${redColors.light}, ${redColors.dark})`;

type MobileDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

const MobileDrawer: React.FC<MobileDrawerProps> = ({ isOpen, onClose }: MobileDrawerProps) => {
  const { pathname } = useLocation();

  return (
    <nav>
      <Drawer
        variant='temporary'
        open={isOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // better open performance on mobile.
        }}
        sx={{
          display: {
            xs: 'block',
            md: 'none',
          },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 'min(85%, 320px)',
            bgcolor: bgColors.light,
            backgroundImage: `radial-gradient(ellipse 140% 40% at 100% 0%, ${glowColor}, transparent 60%)`,
            borderRight: panelBorder,
            boxShadow: '20px 0 60px -20px rgba(0,0,0,0.7)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
            <Box onClick={onClose}>
              <Logo />
            </Box>
            <IconButton aria-label='Sulje valikko' onClick={onClose} size='small'>
              <CloseIcon sx={{ color: LIGHT_TEXT_COLOR }} />
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: 'rgba(238,240,242,0.1)' }} />
          <List sx={{ px: 1.5, py: 2, flexGrow: 1 }} onClick={onClose}>
            {navItems.map(({ name, path }) => {
              const isSelected = path === pathname;

              return (
                <ListItem key={name} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to={path}
                    disableRipple={getDisableRipple(isSelected)}
                    sx={{
                      borderRadius: '10px',
                      borderLeft: '3px solid',
                      borderLeftColor: isSelected ? PRIMARY_COLOR : 'transparent',
                      bgcolor: isSelected ? alpha(redColors.dark, 0.16) : 'transparent',
                      pl: isSelected ? 1.75 : 2,
                      '&:hover': {
                        bgcolor: isSelected ? alpha(redColors.dark, 0.22) : 'rgba(238,240,242,0.06)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={name}
                      primaryTypographyProps={{ variant: 'p3', fontWeight: isSelected ? 600 : 400 }}
                      sx={{
                        color: isSelected ? LIGHT_TEXT_COLOR : 'rgba(238,240,242,0.75)',
                        cursor: getCursor(isSelected),
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          <Box sx={{ p: 2 }}>
            <Button
              component='a'
              href={`tel:${PHONE_NUMBER}`}
              disableRipple
              fullWidth
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                py: 1.25,
                borderRadius: 999,
                background: PHONE_GRADIENT,
                color: LIGHT_TEXT_COLOR,
                textTransform: 'none',
                boxShadow: `0 0 30px -8px ${redColors.dark}`,
                '&:hover': { background: PHONE_GRADIENT },
              }}
            >
              <PhoneIcon sx={{ fontSize: 18 }} />
              <Typography variant='p3b'>{PHONE_NUMBER_DISPLAY}</Typography>
            </Button>
          </Box>
        </Box>
      </Drawer>
    </nav>
  );
};

export default MobileDrawer;
